import React from 'react'
// libs
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { formValueSelector, isDirty } from 'redux-form'
// themes
import { colors } from 'themes'
// utils
import { darken } from 'polished'

const StyledLink = styled.div`
  color: ${colors.blue500};
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  margin-top: 4px;
  width: 72px;

  &:hover {
    color: ${darken(0.1, colors.blue500)};
  }
`

const lineBreakRegExp = /\r?\n|\r/gi

const getShouldTruncate = (text: string, limit: number) => {
  return text.length > limit || (text.match(lineBreakRegExp) || []).length > 2
}

const truncate = (text: string, limit: number, ending = '...') => {
  let result = text.substring(0, limit - ending.length)

  result = result
    .split(lineBreakRegExp)
    .filter((part) => !!part)
    .slice(0, 2)
    .join('\n')

  return result + ending
}

interface Props {
  form: string
  limit?: number
  fieldName: string
  LinkComponent?: React.FC<any>
  shrunkContent: any
  expandedContent: any
}

const ReadMore: React.FC<Props> = ({
  form,
  limit = 160,
  fieldName,
  LinkComponent = StyledLink,
  shrunkContent,
  expandedContent
}) => {
  const selector = formValueSelector(form)

  const { fieldValue, isFieldDirty } = useSelector((state) => ({
    fieldValue: selector(state, fieldName),
    isFieldDirty: isDirty(form)(state, fieldName)
  }))

  const text = fieldValue || ''
  const shouldBeTruncated = getShouldTruncate(text, limit)

  const [shouldShowLink, setShouldShowLink] = React.useState(shouldBeTruncated)
  const [isExpanded, setExpanded] = React.useState(false)

  const shouldShowExpanded = isExpanded || !shouldBeTruncated || isFieldDirty

  const inputRef = React.useRef(null)

  const moveCursorToEnd = (el: HTMLInputElement | HTMLTextAreaElement) => {
    if (!el) {
      return
    }

    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
  }

  const handleViewMoreClick = async () => {
    setExpanded(true)
    moveCursorToEnd(inputRef.current)
  }

  const handleShowLessClick = () => {
    setExpanded(false)
  }

  const recalculate = () => {
    const shouldTruncate = getShouldTruncate(text, limit)

    if (!isFieldDirty) {
      setShouldShowLink(shouldTruncate)

      if (shouldTruncate) {
        setExpanded(false)
      }
    }
  }

  React.useEffect(() => {
    const shouldTruncate = getShouldTruncate(text, limit)

    if (shouldShowLink !== shouldTruncate && !isFieldDirty) {
      setShouldShowLink(shouldTruncate)
    }

    if (isFieldDirty) {
      setShouldShowLink(false)
    }
  }, [text, isFieldDirty])

  return (
    <>
      {shouldShowLink && !isExpanded && shrunkContent({ truncated: truncate(text, limit), recalculate })}
      {shouldShowLink && !isExpanded && <LinkComponent onClick={handleViewMoreClick}>VIEW MORE</LinkComponent>}
      {shouldShowExpanded && expandedContent({ text, recalculate, inputRef })}
      {shouldShowLink && isExpanded && <LinkComponent onClick={handleShowLessClick}>SHOW LESS</LinkComponent>}
    </>
  )
}

export default ReadMore
